<template>
  <el-dialog
    title="非券订单佣金手动结佣"
    :visible.sync="dialogVisible"
    :close-on-click-modal="false"
    :close-on-press-escape="!settleLoading"
    :before-close="handleBeforeClose"
    append-to-body
    width="510px"
    custom-class="non-coupon-manual-settlement-dialog"
    @close="invalidateQuery"
    @closed="handleClosed"
  >
    <div v-loading="queryLoading" class="manual-settlement">
      <div class="manual-settlement__notice">只结算上月及以前的佣金。</div>
      <div class="manual-settlement__row">已选中 {{ userUUIDs.length }}</div>
      <div class="manual-settlement__row manual-settlement__amount-row">
        <span>总结佣金额：</span>
        <strong>{{ displayAmount }}</strong>
        <span v-if="amount !== null">元</span>
        <el-button
          v-if="queryError"
          type="text"
          :disabled="settleLoading"
          @click="fetchAmount"
        >重新查询</el-button>
      </div>
      <div v-if="queryError" class="manual-settlement__error">{{ queryError }}</div>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button :disabled="settleLoading" @click="handleClose">取 消</el-button>
      <el-button
        type="primary"
        :loading="settleLoading"
        :disabled="!canSettle"
        @click="handleConfirm"
      >确认结佣</el-button>
    </div>
  </el-dialog>
</template>

<script>
import poi from '@/api/poi'

const PoiModel = new poi()

export default {
  name: 'NonCouponManualSettlement',
  data() {
    return {
      dialogVisible: false,
      userUUIDs: [],
      amount: null,
      queryLoading: false,
      queryError: '',
      settleLoading: false,
      queryRequestId: 0
    }
  },
  computed: {
    displayAmount() {
      return this.amount === null ? '--' : this.formatAmount(this.amount)
    },
    canSettle() {
      return !this.queryLoading && !this.settleLoading && this.amount !== null && this.amount > 0
    }
  },
  methods: {
    open({ userUUIDs = [] } = {}) {
      if (this.settleLoading) return
      this.resetState()
      if (
        !Array.isArray(userUUIDs) ||
        userUUIDs.length === 0 ||
        userUUIDs.some(userUUID => typeof userUUID !== 'string' || !userUUID.trim())
      ) {
        this.$message.error('团长UUID不能为空')
        return
      }
      const normalizedUserUUIDs = userUUIDs.map(userUUID => userUUID.trim())
      if (new Set(normalizedUserUUIDs).size !== normalizedUserUUIDs.length) {
        this.$message.error('团长UUID数据异常，请刷新后重试')
        return
      }
      this.userUUIDs = normalizedUserUUIDs
      this.dialogVisible = true
      this.fetchAmount()
    },
    async fetchAmount() {
      if (this.queryLoading || this.settleLoading || this.userUUIDs.length === 0) return
      const requestId = ++this.queryRequestId
      const userUUIDs = [...this.userUUIDs]
      const uuidKey = userUUIDs.join(',')
      this.amount = null
      this.queryError = ''
      this.queryLoading = true
      try {
        const res = await PoiModel.getUnsettledNonCouponCommission({ userUUIDs })
        if (!this.isCurrentQuery(requestId, uuidKey)) return
        if (!res || !res.data || res.data.code !== 200) {
          const message = res && res.data && res.data.message
            ? res.data.message
            : '获取待结佣金额失败'
          this.queryError = message
          this.$message.error(message)
          return
        }
        const amount = this.normalizeAmount(res.data.data)
        if (amount === null) {
          this.queryError = '待结佣金额数据异常，请重新查询'
          this.$message.error(this.queryError)
          return
        }
        this.amount = amount
      } catch (error) {
        if (!this.isCurrentQuery(requestId, uuidKey)) return
        const message = this.getErrorMessage(error, '获取待结佣金额失败')
        this.queryError = message
        this.$message.error(message)
      } finally {
        if (requestId === this.queryRequestId) {
          this.queryLoading = false
        }
      }
    },
    async handleConfirm() {
      if (!this.canSettle) return
      const userUUIDs = [...this.userUUIDs]
      this.settleLoading = true
      try {
        const res = await PoiModel.manualSettleNonCouponCommission({ userUUIDs })
        if (!res || !res.data || res.data.code !== 200) {
          const message = res && res.data && res.data.message ? res.data.message : '结佣失败'
          this.amount = null
          this.queryError = `${message}，请重新查询待结佣金额后再操作。`
          this.$message.error(message)
          return
        }
        const actualAmount = this.normalizeAmount(res.data.data)
        const successMessage = actualAmount === null
          ? (res.data.message || '结佣成功')
          : `${res.data.message || '结佣成功'}，本次实际结佣金额：${this.formatAmount(actualAmount)}元`
        this.$message.success(successMessage)
        this.dialogVisible = false
        this.$emit('success')
      } catch (error) {
        this.amount = null
        if (error && error.response) {
          const message = this.getErrorMessage(error, '结佣失败')
          this.queryError = `${message}，请重新查询待结佣金额后再操作。`
          this.$message.error(message)
        } else {
          this.queryError = '结佣结果暂未确认，请重新查询待结佣金额后再操作。'
          this.$message.error(this.queryError)
        }
      } finally {
        this.settleLoading = false
      }
    },
    normalizeAmount(value) {
      if (value === null || value === undefined || value === '') return null
      const amount = Number(value)
      return isFinite(amount) && amount >= 0 ? amount : null
    },
    formatAmount(value) {
      return Number(value).toFixed(2)
    },
    isCurrentQuery(requestId, uuidKey) {
      return requestId === this.queryRequestId &&
        this.dialogVisible &&
        uuidKey === this.userUUIDs.join(',')
    },
    getErrorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error && error.message
          ? error.message
          : fallback
    },
    handleClose() {
      if (this.settleLoading) return
      this.dialogVisible = false
    },
    handleBeforeClose(done) {
      if (this.settleLoading) return
      done()
    },
    invalidateQuery() {
      this.queryRequestId += 1
      this.queryLoading = false
    },
    handleClosed() {
      this.resetState()
    },
    resetState() {
      this.invalidateQuery()
      this.dialogVisible = false
      this.userUUIDs = []
      this.amount = null
      this.queryLoading = false
      this.queryError = ''
      this.settleLoading = false
    }
  }
}
</script>

<style scoped lang="less">
.manual-settlement {
  min-height: 120px;

  &__notice {
    padding: 8px 20px;
    margin-bottom: 16px;
    background: #f5f7fa;
    color: #606266;
  }

  &__row {
    margin-bottom: 18px;
    color: #606266;
    line-height: 22px;
  }

  &__amount-row {
    display: flex;
    align-items: center;
    gap: 4px;

    strong {
      color: #303133;
      font-weight: 500;
    }

    .el-button {
      margin-left: 8px;
    }
  }

  &__error {
    color: #f56c6c;
    line-height: 20px;
  }
}

/deep/ .non-coupon-manual-settlement-dialog {
  max-width: calc(100vw - 32px);

  .el-dialog__body {
    max-height: calc(100vh - 180px);
    overflow-y: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/pc/miniProgram/poiCps/components/commissioSetting/nonCouponManualSettlement.vue